import { head } from 'ramda';
import { matchPath } from 'react-router';
import { createSelector } from 'reselect';

import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';
import { CHECKLIST_DETAILED_PATHS } from 'modules/checklists/constants';
import { getGroupById } from 'modules/groups/selectors';
import { getLocale } from 'modules/i18n/selectors/data';
import { getCurrentUserIsReadOnly, getCurrentUserRoles } from 'modules/users/selectors/currentUser';
import type { RootState } from 'reducers/index';
import {
  getDealerById,
  getValueAddedTax,
  getDealersIsFetching,
  getDealerDefaultLanguage as getDealerDefaultLanguageSelector,
} from 'selectors/dealerSelectors';
import { getLanguages as rawGetAllLanguages } from 'selectors/languageSelectors';
import { getCurrentLocation } from 'selectors/router';
import { isChecklistEditable } from 'services/ChecklistUtils';
import { getDealerLanguage } from 'services/DealerUtils';
import { uniq } from 'utils/ObjectUtils';
import { getProps } from 'utils/selectors';

import { getAllChecklists, getUI } from '.';
import * as Utils from '../utils';

const getDetails = createSelector(getUI, ui => ui.details);

export const getSelectedChecklistId = createSelector(getCurrentLocation, (pathname = '') =>
  CHECKLIST_DETAILED_PATHS.reduce<string | undefined>(
    (checklistId, { path }) => checklistId ?? matchPath(path, pathname)?.params.checklistId,
    undefined
  ));

export const isNewChecklist = createSelector(getSelectedChecklistId, checklistId => checklistId === NEW_ENTITY_ID);

export const getNewChecklistTarget = createSelector(getDetails, state => state.newChecklistTarget);

const getEmptyChecklist = createSelector(getNewChecklistTarget, ({ dealerId, groupId }) =>
  Utils.getEmptyChecklist({ dealerId: dealerId ?? undefined, groupId: groupId ?? undefined }));

const getSelectedChecklist = createSelector(getAllChecklists, getSelectedChecklistId, (checklists, id) =>
  (id != null ? checklists[id] : undefined) ?? {});

/**
 * ⚠️ This selector should only be used in configuration/management pages
 *   because it doesn't take into account the intervention's VAT.
 */
export const getChecklist = createSelector(
  getSelectedChecklist,
  isNewChecklist,
  getEmptyChecklist,
  getValueAddedTax,
  (checklist, isNew, emptyChecklist, vat) => Utils.formatWithVat(isNew ? emptyChecklist : checklist, vat)
) as unknown as (state: RootState) => ReturnType<typeof Utils.formatWithVat>;

const getChecklistGroup = (state: RootState) => {
  const checklist = getChecklist(state);
  return getGroupById(state, checklist.groupId);
};

const getChecklistDealer = (state: RootState) => {
  const checklist = getChecklist(state);
  // @ts-expect-error getDealerById selector is not migrated to TypeScript yet
  return getDealerById(state, checklist.dealerId);
};

export const checklistTarget = createSelector(
  getChecklist,
  getChecklistGroup,
  getChecklistDealer,
  (checklist, group, dealer) => (checklist.dealerId ? dealer : group) ?? {}
);

export const isEditable = createSelector(
  getChecklist,
  getCurrentUserRoles,
  getCurrentUserIsReadOnly,
  (checklist, roles, readonly) => !readonly && isChecklistEditable(checklist, roles)
);

const getDealerDefaultLanguage = createSelector(
  getDealerDefaultLanguageSelector,
  getLocale,
  getChecklistDealer,
  (dealerLocale, locale, dealer) => getDealerLanguage(dealer) ?? dealerLocale ?? locale ?? DEFAULT_LANGUAGE
);

export const getLanguages = createSelector(getDetails, getDealerDefaultLanguage, (details, locale) =>
  uniq([locale, ...details.languages]));

const getAllLanguages = rawGetAllLanguages as unknown as (state: RootState) => string[];

export const getLanguagesNotSelected = createSelector(getAllLanguages, getLanguages, (all, languages) =>
  all.filter((language: string) => !languages.includes(language)));

export const getDefaultLanguage = createSelector(
  getLanguages,
  getDealerDefaultLanguage,
  (languages, dealerLocale) => languages.find(locale => locale === dealerLocale) ?? head(languages)
);

export const getSelectedLocale = createSelector(
  getDetails,
  getLanguages,
  getDefaultLanguage,
  ({ selectedLocale }, languages, defaultLanguage) => {
    const isLocale = (locale: string) => locale === selectedLocale;
    return languages.find(isLocale) ?? defaultLanguage;
  }
);

export const isDefaultLanguageSelected = createSelector(
  getDefaultLanguage,
  getSelectedLocale,
  (defaultLocale, locale) => locale === defaultLocale
);

export const isLoading = createSelector(
  getDetails,
  getDealersIsFetching,
  (details, isDealerLoading) => details.isLoading || isDealerLoading
);

export const isReady = createSelector(getDetails, details => details.isReady);

const patchingAdviceRoot = createSelector(getDetails, details => details.patchingAdvice);

type PatchingAdviceProps = { checklistId: string; adviceId: string };

export const patchingAdviceState = createSelector(
  patchingAdviceRoot,
  getProps<PatchingAdviceProps>,
  (state, { checklistId, adviceId }) => state[`${checklistId}-${adviceId}`]
);

export const getApiError = createSelector(getDetails, state => state.error);
